import type {
  CsatKeywordHit,
  CsatAnalysisSettings,
  CsatComplianceCategoryKey,
} from 'src/pages/csat/slice/csatTypes';

export type { CsatComplianceCategoryKey };

export const CSAT_COMPLIANCE_CATEGORY_KEYS: CsatComplianceCategoryKey[] = [
  'open',
  'close',
  'communication',
  'legalDisclaimer',
  'responsibility',
  'salesProcess',
];

export const CSAT_ANALYSIS_METADATA_KEYS = [
  'keywordAnalysis',
  'complianceAnalysis',
  'semanticAnalysis',
] as const;

/** Shown in dedicated voice CSAT UI — hide from generic "other details". */
export const CSAT_VOICE_METADATA_KEYS = [
  'audioText',
  'callId',
  'transcriptLength',
  'sttProvider',
  'sttLanguage',
  'channelType',
  'channel',
  'messageCount',
] as const;

export function mergeCsatAnalysisSettings(
  partial?: CsatAnalysisSettings | null
): CsatAnalysisSettings {
  if (!partial) return {};
  return {
    ...partial,
    compliance: partial.compliance ? { ...partial.compliance } : undefined,
  };
}

export function formatAnalysisValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((v) => formatAnalysisValue(v)).join(', ');
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) return '—';
    return entries.map(([k, v]) => `${k}: ${formatAnalysisValue(v)}`).join('; ');
  }
  return String(value);
}

/** Tracked keyword matches from `metadata.keywordHits`; absent or malformed → []. */
export function getCsatKeywordHits(
  metadata: Record<string, unknown> | null | undefined
): CsatKeywordHit[] {
  const hits = metadata?.keywordHits;
  if (!Array.isArray(hits)) return [];
  return hits
    .filter(
      (hit): hit is Record<string, unknown> =>
        !!hit && typeof hit === 'object' && typeof hit.keyword === 'string' && !!hit.keyword
    )
    .map((hit) => ({
      keyword: hit.keyword as string,
      count: typeof hit.count === 'number' ? hit.count : 1,
      messageIds: Array.isArray(hit.messageIds)
        ? hit.messageIds.filter((id): id is string => typeof id === 'string')
        : [],
    }));
}

export function pickRemainingMetadata(
  metadata: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!metadata) return {};
  const reserved = new Set<string>([
    ...CSAT_ANALYSIS_METADATA_KEYS,
    ...CSAT_VOICE_METADATA_KEYS,
    'confidence',
    'keywordHits',
  ]);
  return Object.fromEntries(
    Object.entries(metadata).filter(([key]) => !reserved.has(key))
  );
}
